import { ButtonHTMLAttributes } from 'react';
import { Check, Copy, NotebookPen, Pencil, RotateCcw, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { Flex } from '@instamenu/ui-system-next';
import { useCopyToClipboard } from '@instamenu/react-hooks';
import posthog from 'posthog-js';
import { useThreadContext } from '../context/context';
import { action } from 'mobx';
import { ChatHistoryItem } from '../types';
import { findLastIndex } from 'lodash-es';
import { collectAttachmentsFromUserTurn } from '../utils/collect-attachments';

export const ConversationItemActions = ({
  item,
  onStartEditing,
}: {
  item: ChatHistoryItem;
  onStartEditing?: (messageId: string) => void;
}) => {
  const { thread } = useThreadContext();
  const { copy, copied } = useCopyToClipboard();
  const [feedback, setFeedback] = useState<'positive' | 'negative'>();

  if (item.type !== 'message') return null;

  if (item.role === 'user' && item.content.type === 'text') {
    return (
      <ActionButton
        onClick={() => onStartEditing?.(item.id)}
        title="Edit prompt"
        disabled={thread.isBusy}
      >
        <Pencil size={16} />
      </ActionButton>
    );
  }

  if (item.role !== 'assistant') return null;

  const handleCopy = async () => {
    if (item.content.type !== 'text') return;
    await copy(item.content.text);
  };

  const handleFeedback = async (value: 'positive' | 'negative') => {
    if (feedback) return;
    setFeedback(value);
    posthog.capture('ai_prompt_feedback', { feedback: value, responseId: item.responseId });
  };

  const handleRunAgain = () => {
    const currentIndex = thread.history.findIndex((h) => h.id === item.id);

    const userMessageIndex = findLastIndex(
      thread.history.slice(0, currentIndex),
      (h) => h.role === 'user' && h.type === 'message',
    );

    if (userMessageIndex === -1) return;

    const userMessage = thread.history[userMessageIndex];
    if (userMessage.type !== 'message' || userMessage.content.type !== 'text') return;

    const attachments = collectAttachmentsFromUserTurn(thread, userMessageIndex);

    posthog.capture('ai_prompt_rerun', {
      responseId: item.responseId,
      message: userMessage.content.text,
    });

    thread.resetToIndex(userMessageIndex + 1);
    thread.post({ message: userMessage.content.text, attachments }, true);
  };

  if (item.type === 'message' && item.content.type === 'text') {
    return (
      <Flex style={{ gap: 8, padding: 4, alignItems: 'center' }}>
        <ActionButton onClick={handleRunAgain} title="Run again">
          <RotateCcw size={16} />
        </ActionButton>
        <ActionButton onClick={handleCopy} title="Copy">
          {copied ? <Check size={16} /> : <Copy size={16} />}
        </ActionButton>
        <ActionButton
          onClick={action(() => {
            if (item.content.type !== 'text') return;
            thread.notepad = item.content.text;
            thread.showCanvas('notepad');
          })}
        >
          <NotebookPen size={16} />
        </ActionButton>
        {feedback !== 'negative' && (
          <ActionButton onClick={() => handleFeedback('positive')} title="Thumbs up">
            <ThumbsUp size={16} color={feedback && 'green'} />
          </ActionButton>
        )}
        {feedback !== 'positive' && (
          <ActionButton onClick={() => handleFeedback('negative')} title="Thumbs down">
            <ThumbsDown size={16} color={feedback && 'red'} />
          </ActionButton>
        )}
      </Flex>
    );
  }
  return null;
};

const ActionButton = (props: ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      style={{
        background: 'rgba(255,255,255,0.85)',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        padding: 4,
        display: 'flex',
        alignItems: 'center',
      }}
      {...props}
    />
  );
};
